'use client'

// Kalender bulanan — task nempel di tanggal deadline-nya. Klik → drawer.
import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, X, Plus } from 'lucide-react'
import type { Task } from '@/lib/types'
import { isoDay, type RiskCtx } from '@/lib/dev-sprint'
import type { BoardData, Filters, DevDerived } from './types'
import { taskMatchesFilters } from './filter'

const MONTHS = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember']
const DOW = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min']
const pad = (n: number) => String(n).padStart(2, '0')
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`

interface Props {
  board: BoardData
  derived: DevDerived
  riskCtx: RiskCtx
  filters: Filters
  today: string
  isLead: boolean
  onOpenTask: (id: string) => void
  onAddTask: (dueDate: string) => void
}

export function CalendarView({ board, derived, riskCtx, filters, today, isLead, onOpenTask, onAddTask }: Props) {
  const { statusOf, projById } = derived
  const [cursor, setCursor] = useState(() => ({ y: +today.slice(0, 4), m: +today.slice(5, 7) - 1 }))
  const [dayOpen, setDayOpen] = useState<string | null>(null)

  const byDay = useMemo(() => {
    const map = new Map<string, Task[]>()
    for (const t of board.tasks) {
      const d = isoDay(t.dueDate)
      if (!d) continue
      if (!taskMatchesFilters(t, filters, derived, riskCtx)) continue
      const a = map.get(d) ?? []
      a.push(t)
      map.set(d, a)
    }
    return map
  }, [board.tasks, filters, derived, riskCtx])

  // Grid: mulai Senin minggu yang memuat tgl 1, 6 baris (42 sel).
  const first = new Date(Date.UTC(cursor.y, cursor.m, 1))
  const startDow = (first.getUTCDay() + 6) % 7 // 0=Senin
  const cells: { iso: string; inMonth: boolean; day: number }[] = []
  for (let i = 0; i < 42; i++) {
    const dt = new Date(Date.UTC(cursor.y, cursor.m, 1 - startDow + i))
    cells.push({
      iso: ymd(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()),
      inMonth: dt.getUTCMonth() === cursor.m,
      day: dt.getUTCDate(),
    })
  }

  const move = (delta: number) => {
    const m = cursor.m + delta
    setCursor({ y: cursor.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 })
  }

  return (
    <div className="rounded-2xl border border-[#E7E5DF] bg-white">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#EDEBE5]">
        <h3 className="text-[15px] font-bold text-[#1A1916]">{MONTHS[cursor.m]} {cursor.y}</h3>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => move(-1)} className="p-1.5 rounded-lg text-[#6B6A65] hover:bg-[#F2F1EC]"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor({ y: +today.slice(0, 4), m: +today.slice(5, 7) - 1 })} className="px-2.5 py-1.5 rounded-lg text-[12px] font-semibold text-[#6B6A65] hover:bg-[#F2F1EC]">Hari ini</button>
          <button onClick={() => move(1)} className="p-1.5 rounded-lg text-[#6B6A65] hover:bg-[#F2F1EC]"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-[#EDEBE5]">
        {DOW.map((d) => <div key={d} className="px-2 py-2 text-[11px] font-bold uppercase tracking-wide text-[#AEADA8] text-center">{d}</div>)}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((c, i) => {
          const tasks = byDay.get(c.iso) ?? []
          const isToday = c.iso === today
          return (
            <div key={i} className={`group min-h-[96px] border-b border-r border-[#F2F1EC] p-1.5 ${c.inMonth ? '' : 'bg-[#FAFAF8]'} ${i % 7 === 6 ? 'border-r-0' : ''}`}>
              <div className="flex items-center justify-between mb-1">
                <div className={`text-[11px] font-semibold tabular-nums w-5 h-5 flex items-center justify-center rounded-full ${isToday ? 'bg-[#1A1916] text-white' : c.inMonth ? 'text-[#4A4844]' : 'text-[#C9C7C0]'}`}>{c.day}</div>
                {isLead && (
                  <button onClick={() => onAddTask(c.iso)} title="Tambah task deadline hari ini" className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded-md flex items-center justify-center text-[#8B8A85] hover:bg-[#F0EEE7] hover:text-[#1A1916] transition-all">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-1">
                {tasks.slice(0, 3).map((t) => {
                  const p = projById.get(t.projectId)
                  const done = statusOf(t) === 'done'
                  const late = !done && c.iso < today
                  return (
                    <button
                      key={t.id}
                      onClick={() => onOpenTask(t.id)}
                      title={t.title}
                      className="w-full flex items-center gap-1 px-1 py-0.5 rounded text-left hover:opacity-80"
                      style={{ backgroundColor: `${p?.color ?? '#D4A843'}1F` }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: late ? '#C0362C' : (p?.color ?? '#D4A843') }} />
                      <span className={`text-[10.5px] truncate ${done ? 'line-through text-[#AEADA8]' : 'text-[#4A4844]'}`}>{t.title}</span>
                    </button>
                  )
                })}
                {tasks.length > 3 && (
                  <button onClick={() => setDayOpen(c.iso)} className="text-[10px] font-medium text-[#6B6A65] px-1 hover:text-[#1A1916] hover:underline">+{tasks.length - 3} lagi</button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Popover semua task di satu hari */}
      {dayOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDayOpen(null)} />
          <div className="relative w-full max-w-[380px] max-h-[70vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-[#EDEBE5] px-4 py-3 flex items-center justify-between">
              <h3 className="text-[14px] font-bold text-[#1A1916]">{new Date(dayOpen + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
              <button onClick={() => setDayOpen(null)} className="p-1 rounded-lg text-[#AEADA8] hover:text-[#1A1916] hover:bg-[#F2F1EC]"><X className="w-4 h-4" /></button>
            </div>
            <ul className="p-2">
              {(byDay.get(dayOpen) ?? []).map((t) => {
                const p = projById.get(t.projectId)
                const done = statusOf(t) === 'done'
                return (
                  <li key={t.id}>
                    <button onClick={() => { onOpenTask(t.id); setDayOpen(null) }} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-[#FAF9F6] text-left transition-colors">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p?.color ?? '#D4A843' }} />
                      <span className={`text-[13px] truncate flex-1 ${done ? 'line-through text-[#AEADA8]' : 'text-[#1A1916]'}`}>{t.title}</span>
                      <span className="text-[11px] text-[#AEADA8]">{t.assignee || '—'}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
